import { createSlice } from '@reduxjs/toolkit';
import { get, post, postMultipart } from 'api/fetch';
import { formatToTimeZone } from 'date-fns-timezone';
import { FEATURE_AMBASSADOR_DASHBOARD_NI_THREE } from 'consts';

const slice = createSlice({
  name: 'global/AmbassadorDashboard',
  initialState: {
    ambassadorPaymentDetails: null,
    contract: null,
    newCampaign: null,
    campaigns: [],
    ambassadorStatistics: {},
    isLoading: false,
    error: null
  },
  reducers: {
    fetchAmbassadorContract: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchAmbassadorContractSuccess: (state, { payload }) => {
      state.contract = `data:application/pdf;base64,${payload}`;
      state.isLoading = false;
      state.error = null;
    },
    fetchAmbassadorContractFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    fetchAmbassadorPaymentDetails: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchAmbassadorPaymentDetailsSuccess: (state, { payload }) => {
      state.ambassadorPaymentDetails = { ...state.ambassadorPaymentDetails, ...payload };
      state.isLoading = false;
      state.error = null;
    },
    fetchAmbassadorPaymentDetailsFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    updateAmbassador: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    updateAmbassadorSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    updateAmbassadorFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    createCampaign: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    createCampaignSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.newCampaign = payload;
      state.error = null;
    },
    createCampaignFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    fetchCampaigns: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchCampaignsSuccess: (state, { payload }) => {
      state.campaigns = [...payload];
      state.isLoading = false;
      state.error = null;
    },
    fetchCampaignsFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    setNewCampaignToNull: (state) => {
      state.newCampaign = null;
    },
    fetchAmbassadorStatistics: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchAmbassadorStatisticsSuccess: (state, { payload }) => {
      state.ambassadorStatistics = { ...payload };
      state.isLoading = false;
      state.error = null;
    },
    fetchAmbassadorStatisticsFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    }
  }
});

export default slice.reducer;

const { actions } = slice;

export const fetchAmbassadorPaymentDetails = () => async (dispatch) => {
  dispatch(actions.fetchAmbassadorPaymentDetails());

  try {
    const response = await get('/affiliate/payment-details');
    dispatch(actions.fetchAmbassadorPaymentDetailsSuccess(response));
  } catch (error) {
    dispatch(actions.fetchAmbassadorPaymentDetailsFailure(error));
  }
};

export const ssoIntoCircle = () => async (dispatch) => {
  await get('/circle/oauth/redirect');
};

export const fetchAmbassadorContract = () => async (dispatch) => {
  dispatch(actions.fetchAmbassadorContract());

  try {
    const response = await get('/affiliate/contract');
    dispatch(actions.fetchAmbassadorContractSuccess(response));
  } catch (error) {
    dispatch(actions.fetchAmbassadorContractFailure(error));
  }
};

export const updateAmbassador = (ambassador) => async (dispatch) => {
  dispatch(actions.updateAmbassador());
  try {
    await postMultipart('/affiliate/update', ambassador);
    await dispatch(actions.updateAmbassadorSuccess());
  } catch (error) {
    dispatch(actions.updateAmbassadorFailure(error));
  }
};

export const createCampaign = (info) => async (dispatch) => {
  dispatch(actions.createCampaign());
  try {
    const response = await post('/affiliate/campaign', info);
    dispatch(actions.createCampaignSuccess(response));
  } catch (error) {
    dispatch(actions.createCampaignFailure(error));
  }
};

export const fetchCampaigns = () => async (dispatch) => {
  dispatch(actions.fetchCampaigns());

  try {
    const response = await get('/affiliate/campaigns');

    response.forEach(
      (c) =>
        (c.created = formatToTimeZone(new Date(c.created), 'ddd, MMMM D, YYYY', {
          timeZone: 'Etc/UTC'
        }))
    );

    dispatch(actions.fetchCampaignsSuccess(response));
  } catch (error) {
    dispatch(actions.fetchCampaignsFailure(error));
  }
};

export const setNewCampaignToNull = () => (dispatch) => {
  dispatch(actions.setNewCampaignToNull());
};

export const fetchAmbassadorStatistics =
  (userID, interval, startDate, endDate, campaignID) => async (dispatch) => {
    dispatch(actions.fetchAmbassadorStatistics());

    let url = FEATURE_AMBASSADOR_DASHBOARD_NI_THREE
      ? `/ambassador/data/statistics/${userID}?interval=${interval}`
      : `/ambassador/data/statistics/ni/${userID}?interval=${interval}`;

    if (startDate !== 'undefined') {
      url = `${url}&startDate=${startDate}`;
    }
    if (endDate !== 'undefined') {
      url = `${url}&endDate=${endDate}`;
    }
    if (campaignID !== 'undefined') {
      url = `${url}&campaignID=${campaignID}`;
    }

    try {
      const response = await get(url, { isAmbassador: true });
      dispatch(actions.fetchAmbassadorStatisticsSuccess(response));
    } catch (error) {
      dispatch(actions.fetchAmbassadorStatisticsFailure(error));
    }
  };
